import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import { useLocale, tr, t, pickLocalized, DEFAULT_LOCALE } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/ui/form-input";
import { FormTextarea } from "@/components/ui/form-textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { sendContact, getTurnstileSiteKey } from "@/lib/contact.functions";
import { sanityClient } from "@/lib/sanity";
import { useServerFn } from "@tanstack/react-start";
import { pageMeta } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  loader: async () => {
    const [{ siteKey }, page] = await Promise.all([
      getTurnstileSiteKey(),
      sanityClient.fetch<any>(`*[_type == "sitePage" && pageKey == "contact"][0]{ seo }`),
    ]);
    return { siteKey, page };
  },
  // Sitekey is constant per deployment — don't refire the RPC on client nav.
  staleTime: Infinity,
  head: ({ loaderData }) => ({
    ...pageMeta({
      title: pickLocalized(loaderData?.page?.seo?.metaTitle, DEFAULT_LOCALE) || "Contact — Matchamind",
      description: pickLocalized(loaderData?.page?.seo?.metaDescription, DEFAULT_LOCALE) || undefined,
      path: "/contact",
    }),
    scripts: [
      {
        src: "https://challenges.cloudflare.com/turnstile/v0/api.js",
        async: true,
        defer: true,
      },
    ],
  }),
});

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const copy = {
  eyebrow: { en: "CONTACT US", fr: "NOUS CONTACTER" },
  titlePart1: { en: "Get in touch", fr: "Entrer en contact" },
  titlePart2: { en: "with us.", fr: "avec nous." },
  intro1: {
    en: "Any ",
    fr: "Des ",
  },
  introQuestions: { en: "questions", fr: "questions" },
  introOr: { en: " or ", fr: " ou des demandes de " },
  introPartnership: { en: "partnership", fr: "partenariat" },
  introTail: { en: " inquiries ? ✉️", fr: " ? ✉️" },
  haveQ: {
    en: "Have a question about the app?",
    fr: "Une question sur l'application ?",
  },
  findAnswer: {
    en: "You might find the answer in our ",
    fr: "Vous trouverez peut-être la réponse dans notre ",
  },
  faq: { en: "FAQ", fr: "FAQ" },
  fullName: { en: "Full Name", fr: "Nom complet" },
  email: { en: "Email", fr: "Email" },
  subject: { en: "Subject", fr: "Sujet" },
  messagePh: {
    en: "Write your message here...",
    fr: "Écrivez votre message ici...",
  },
  namePh: { en: "Aria Stark", fr: "Aria Stark" },
  emailPh: { en: "myemail@gmail.com", fr: "monemail@gmail.com" },
  subjectPh: { en: "Subject of your message", fr: "Sujet de votre message" },
  consent: {
    en: "I consent to Anatol Labs, publisher of the Matchamind app, processing my contact information for the sole purpose of responding to my inquiry. I reserve the right to access, correct, delete, or opt out of the processing of my personal data at any time by submitting a written request to privacy@matcha-mind.com.",
    fr: "J'accepte qu'Anatol Labs, éditeur de l'application Matchamind, utilise mes coordonnées pour répondre à ma demande de contact. Je peux exercer à tout moment mes droits d'accès, de rectification, de suppression, ou d'opposition à ces données en adressant un mail à privacy@matcha-mind.com.",
  },
} as const;

const errMsg = {
  nameMin: {
    en: "Name must be at least 2 characters.",
    fr: "Le nom doit contenir au moins 2 caractères.",
  },
  emailRequired: {
    en: "Email address is required.",
    fr: "L'adresse email est obligatoire.",
  },
  emailInvalid: {
    en: "Please enter a valid email address.",
    fr: "Veuillez entrer une adresse email valide.",
  },
  subjectRequired: {
    en: "Subject is required.",
    fr: "L'objet est obligatoire.",
  },
  messageRequired: {
    en: "Description is required.",
    fr: "La description est obligatoire.",
  },
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Values = { name: string; email: string; subject: string; message: string };
type Touched = Record<keyof Values, boolean>;

function validate(v: Values, locale: "en" | "fr"): Partial<Record<keyof Values, string>> {
  const errs: Partial<Record<keyof Values, string>> = {};
  if (v.name.trim().length < 2) errs.name = tr(errMsg.nameMin, locale);
  const email = v.email.trim();
  if (!email) errs.email = tr(errMsg.emailRequired, locale);
  else if (!EMAIL_RE.test(email)) errs.email = tr(errMsg.emailInvalid, locale);
  if (!v.subject.trim()) errs.subject = tr(errMsg.subjectRequired, locale);
  if (!v.message.trim()) errs.message = tr(errMsg.messageRequired, locale);
  return errs;
}

function ContactPage() {
  const locale = useLocale();
  const { siteKey } = Route.useLoaderData();
  const send = useServerFn(sendContact);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [values, setValues] = useState<Values>({ name: "", email: "", subject: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [touched, setTouched] = useState<Touched>({
    name: false,
    email: false,
    subject: false,
    message: false,
  });

  const errors = validate(values, locale);
  const isValid = Object.keys(errors).length === 0 && consent && captchaToken.length > 0;

  // Render Turnstile widget once script is loaded
  useEffect(() => {
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile || !captchaRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(captchaRef.current, {
        sitekey: siteKey,
        language: locale,
        theme: "light",
        size: "flexible",
        appearance: "always",
        callback: (token: string) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
    };
    const id = window.setInterval(tryRender, 200);
    tryRender();
    return () => {
      cancelled = true;
      window.clearInterval(id);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [locale, siteKey]);

  const setField = (k: keyof Values) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Read the value synchronously: React nulls e.currentTarget after the event
    // handler returns, so reading it inside the deferred updater throws.
    const value = e.currentTarget.value;
    setValues((prev) => ({ ...prev, [k]: value }));
  };
  const markTouched = (k: keyof Values) => () => setTouched((prev) => ({ ...prev, [k]: true }));

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({ name: true, email: true, subject: true, message: true });
    if (!isValid) return;
    setStatus("loading");
    try {
      await send({
        data: {
          name: values.name.trim(),
          email: values.email.trim(),
          subject: values.subject.trim(),
          message: values.message.trim(),
          captchaToken,
        },
      });
      setStatus("success");
      setValues({ name: "", email: "", subject: "", message: "" });
      setTouched({ name: false, email: false, subject: false, message: false });
      setConsent(false);
      setCaptchaToken("");
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {}
      }
    } catch {
      setStatus("error");
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {}
      }
      setCaptchaToken("");
    }
  }

  const showErr = (k: keyof Values) => (touched[k] ? errors[k] : undefined);

  return (
    <div className="mx-auto w-full max-w-[1040px] px-6 md:px-10">
      {/* Header section */}
      <section className="flex flex-col items-start gap-6 py-16 md:py-[100px]">
        <div className="inline-flex items-center justify-center rounded-[43px] bg-secondary-300 px-2 py-1">
          <span className="font-sans text-[14px] leading-[14px] font-medium uppercase tracking-[1.4px] text-text-muted">
            {tr(copy.eyebrow, locale)}
          </span>
        </div>
        <h1 className="font-display text-[40px] leading-[44px] font-semibold tracking-[-0.5px]">
          <span className="text-text-primary">{tr(copy.titlePart1, locale)} </span>
          <span className="text-secondary-900">{tr(copy.titlePart2, locale)}</span>
        </h1>
        <div className="font-sans text-[18px] leading-[24px] text-text-secondary">
          <p>
            {tr(copy.intro1, locale)}
            <strong className="font-bold">{tr(copy.introQuestions, locale)}</strong>
            {tr(copy.introOr, locale)}
            <strong className="font-bold">{tr(copy.introPartnership, locale)}</strong>
            {tr(copy.introTail, locale)}
          </p>
          <p className="mt-6">{tr(copy.haveQ, locale)}</p>
          <p>
            {tr(copy.findAnswer, locale)}
            <Link
              to="/faq"
              search={(prev: any) => prev}
              className="font-bold no-underline hover:text-text-primary"
            >
              {tr(copy.faq, locale)}
            </Link>
          </p>
        </div>
      </section>

      {/* Form */}
      <form onSubmit={onSubmit} className="flex flex-col gap-6 pb-16">
        <div className="flex flex-col gap-6 md:flex-row md:gap-3">
          <FormInput
            name="name"
            label={tr(copy.fullName, locale)}
            placeholder={tr(copy.namePh, locale)}
            required
            maxLength={100}
            value={values.name}
            onChange={setField("name")}
            onBlur={markTouched("name")}
            error={showErr("name")}
          />
          <FormInput
            name="email"
            type="email"
            label={tr(copy.email, locale)}
            placeholder={tr(copy.emailPh, locale)}
            required
            maxLength={255}
            value={values.email}
            onChange={setField("email")}
            onBlur={markTouched("email")}
            error={showErr("email")}
          />
        </div>

        <FormInput
          name="subject"
          label={tr(copy.subject, locale)}
          placeholder={tr(copy.subjectPh, locale)}
          required
          maxLength={200}
          containerClassName="md:w-[calc(50%-6px)]"
          value={values.subject}
          onChange={setField("subject")}
          onBlur={markTouched("subject")}
          error={showErr("subject")}
        />

        <FormTextarea
          name="message"
          placeholder={tr(copy.messagePh, locale)}
          required
          maxLength={1000}
          value={values.message}
          onChange={setField("message")}
          onBlur={markTouched("message")}
          error={showErr("message")}
        />

        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            checked={consent}
            onCheckedChange={(v) => setConsent(v === true)}
            required
            className="mt-1 h-5 w-5 border-secondary-700 data-[state=checked]:border-secondary-700 data-[state=checked]:bg-secondary data-[state=checked]:text-text-primary"
          />
          <span className="font-sans text-[14px] leading-[18px] font-light text-primary-300">
            {tr(copy.consent, locale)}
          </span>
        </label>

        {/* No cf-turnstile class: it triggers Turnstile's implicit auto-render
            (with a null data-sitekey → "expected string, got object"); we render
            explicitly via window.turnstile.render on captchaRef instead. */}
        <div ref={captchaRef} className="[&:empty]:hidden" />

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            disabled={!isValid || status === "loading"}
            variant="mainPrimary"
            size="medium"
          >
            <Send className="size-5" />
            {status === "loading" ? tr(t.contact.sending, locale) : tr(t.contact.send, locale)}
          </Button>
        </div>

        {status === "success" && (
          <p className="text-right text-sm text-text-primary">{tr(t.contact.success, locale)}</p>
        )}
        {status === "error" && (
          <p className="text-right text-sm text-destructive">{tr(t.contact.error, locale)}</p>
        )}
      </form>
    </div>
  );
}
